import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  LayoutDashboard, Building2, CheckSquare, FolderKanban, PoundSterling,
  FileText, Bell, MapPin, Search, LogOut, Settings, ChevronRight, Wrench, KeyRound, MessageSquare, Users, Home, ClipboardCheck, Menu, Contact2,
} from "lucide-react";
import { useEffect, useState, useRef } from "react";
import api, { fileUrl } from "@/lib/api";

const nav = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, id: "dashboard" },
  { to: "/properties", label: "Properties", icon: Building2, id: "properties" },
  { to: "/landlords", label: "Landlords", icon: Users, id: "landlords" },
  { to: "/contacts", label: "Contacts", icon: Contact2, id: "contacts" },
  { to: "/tasks", label: "Tasks", icon: CheckSquare, id: "tasks" },
  { to: "/lettings", label: "Lettings", icon: KeyRound, id: "lettings" },
  { to: "/sales", label: "Sales", icon: Home, id: "sales" },
  { to: "/maintenance", label: "Maintenance", icon: Wrench, id: "maintenance" },
  { to: "/projects", label: "Projects", icon: FolderKanban, id: "projects" },
  { to: "/finance", label: "Finance Centre", icon: PoundSterling, id: "finance" },
  { to: "/documents", label: "Documents", icon: FileText, id: "documents" },
  { to: "/notifications", label: "Notifications", icon: Bell, id: "notifications" },
  { to: "/messages", label: "Messages", icon: MessageSquare, id: "messages", adminOnly: true },
  { to: "/settings", label: "Settings", icon: Settings, id: "settings", adminOnly: true },
];

export default function Layout({ children, title, subtitle }) {
  const { user, company, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [unread, setUnread] = useState(0);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState(null);
  const [showResults, setShowResults] = useState(false);
  const searchTimer = useRef(null);
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem("pp_sidebar_collapsed") === "1");

  const toggleCollapse = () => setCollapsed((c) => {
    localStorage.setItem("pp_sidebar_collapsed", c ? "0" : "1");
    return !c;
  });

  useEffect(() => {
    const sp = new URLSearchParams(location.search);
    setQuery(sp.get("q") || "");
  }, [location.search]);

  const onSearch = (v) => {
    setQuery(v);
    clearTimeout(searchTimer.current);
    if (v.trim().length < 2) { setResults(null); setShowResults(false); return; }
    searchTimer.current = setTimeout(() => {
      api.get(`/search?q=${encodeURIComponent(v)}`)
        .then(({ data }) => { setResults(data); setShowResults(true); })
        .catch(() => setResults(null));
    }, 250);
  };

  const submitSearch = () => {
    setShowResults(false);
    navigate(query.trim() ? `/properties?q=${encodeURIComponent(query)}` : "/properties");
  };

  const goto = (path) => { setShowResults(false); navigate(path); };
  const hasResults = results && (results.properties?.length || results.contacts?.length);
  const CONTACT_CLR = { landlord: "text-blue-600", tenant: "text-emerald-600", guarantor: "text-amber-600", contractor: "text-violet-600" };

  useEffect(() => {
    const poll = () => api.get("/notifications").then(({ data }) => {
      setUnread(data.filter((n) => !n.read).length);
    }).catch(() => {});
    poll();
    const iv = setInterval(poll, 60000);
    return () => clearInterval(iv);
  }, []);

  const initials = (user?.name || "U").split(" ").map((n) => n[0]).slice(0, 2).join("");

  return (
    <div className="min-h-screen flex bg-slate-50">
      {/* Sidebar */}
      <aside className={`${collapsed ? "w-20" : "w-72"} bg-slate-900 text-slate-100 flex flex-col fixed inset-y-0 left-0 z-30 transition-[width] duration-300 ease-in-out`}>
        <div className={`h-16 flex items-center border-b border-slate-800 ${collapsed ? "justify-center px-0" : "gap-2.5 px-6"}`}>
          <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center shrink-0">
            <MapPin size={18} className="text-white" />
          </div>
          {!collapsed && <span className="font-display font-bold text-lg tracking-tight">PinPoint</span>}
        </div>

        <div className="px-4 py-4 border-b border-slate-800">
          <div className={`flex items-center ${collapsed ? "justify-center" : "gap-3 px-2"}`} title={collapsed ? company?.name : undefined}>
            {company?.logo_file_id ? (
              <img src={fileUrl(company.logo_file_id)} alt={company?.name} className="w-9 h-9 rounded-lg object-contain bg-white p-0.5 shrink-0" data-testid="header-logo" />
            ) : (
              <div className="w-9 h-9 rounded-lg bg-blue-600/20 border border-blue-500/30 flex items-center justify-center text-blue-300 text-xs font-semibold shrink-0">
                {company?.name?.[0] || "P"}
              </div>
            )}
            {!collapsed && (
              <div className="min-w-0">
                <p className="text-sm font-medium truncate">{company?.name || "Agency"}</p>
                <p className="text-xs text-slate-400">{company?.plan || "Professional"} plan</p>
              </div>
            )}
          </div>
        </div>

        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto overflow-x-hidden">
          {nav.filter((item) => !item.adminOnly || user?.role === "admin").map((item) => (
            <NavLink
              key={item.id}
              to={item.to}
              data-testid={`sidebar-nav-${item.id}`}
              title={collapsed ? item.label : undefined}
              className={({ isActive }) =>
                `relative flex items-center rounded-lg text-sm font-medium transition-colors ${collapsed ? "justify-center px-0 py-3" : "gap-3 px-3 py-2.5"} ${
                  isActive ? "bg-slate-800 text-white" : "text-slate-400 hover:text-white hover:bg-slate-800/50"
                }`
              }
            >
              <item.icon size={18} className="shrink-0" />
              {!collapsed && <span className="flex-1">{item.label}</span>}
              {item.id === "notifications" && unread > 0 && (
                collapsed
                  ? <span className="absolute top-1.5 right-2.5 w-2 h-2 bg-blue-500 rounded-full" />
                  : <span className="bg-blue-600 text-white text-[10px] font-semibold px-1.5 py-0.5 rounded-full">{unread}</span>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="px-3 py-4 border-t border-slate-800">
          <div className={`flex items-center ${collapsed ? "justify-center" : "gap-3 px-3"} py-2`} title={collapsed ? user?.name : undefined}>
            {user?.picture ? (
              <img src={user.picture} alt="" className="w-8 h-8 rounded-full object-cover shrink-0" />
            ) : (
              <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-700 text-xs font-semibold flex items-center justify-center shrink-0">{initials}</div>
            )}
            {!collapsed && (
              <>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium truncate">{user?.name}</p>
                  <p className="text-xs text-slate-400 truncate">{user?.role}</p>
                </div>
                <button data-testid="logout-button" onClick={logout} className="text-slate-400 hover:text-white transition-colors">
                  <LogOut size={16} />
                </button>
              </>
            )}
          </div>
          {collapsed && (
            <button data-testid="logout-button-collapsed" onClick={logout} title="Log out" className="w-full flex items-center justify-center py-2 mt-1 text-slate-400 hover:text-white transition-colors">
              <LogOut size={16} />
            </button>
          )}
        </div>
      </aside>

      {/* Main */}
      <div className={`flex-1 ${collapsed ? "ml-20" : "ml-72"} flex flex-col min-h-screen transition-[margin] duration-300 ease-in-out`}>
        <header className="h-16 bg-white/80 backdrop-blur-xl border-b border-slate-200 flex items-center px-8 sticky top-0 z-20">
          <button data-testid="sidebar-toggle" onClick={toggleCollapse} title={collapsed ? "Expand menu" : "Collapse menu"}
            className="w-9 h-9 -ml-2 mr-3 rounded-lg hover:bg-slate-100 flex items-center justify-center text-slate-600 transition-colors shrink-0">
            <Menu size={18} />
          </button>
          <div className="flex-1 max-w-md relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              data-testid="global-search"
              value={query}
              onChange={(e) => onSearch(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") submitSearch(); if (e.key === "Escape") setShowResults(false); }}
              onFocus={() => { if (hasResults) setShowResults(true); }}
              onBlur={() => setTimeout(() => setShowResults(false), 150)}
              placeholder="Search properties & contacts…"
              className="w-full pl-9 pr-4 py-2 text-sm rounded-lg bg-slate-100 border border-transparent focus:bg-white focus:border-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-600/20 transition-all"
            />
            {showResults && (
              <div data-testid="global-search-results" className="absolute left-0 right-0 top-full mt-2 bg-white rounded-xl shadow-xl border border-slate-200 overflow-hidden z-40 max-h-96 overflow-y-auto">
                {!hasResults ? (
                  <p className="px-4 py-3 text-sm text-slate-400">No matches for “{query}”.</p>
                ) : (
                  <>
                    {results.properties?.length > 0 && (
                      <div>
                        <p className="px-4 pt-3 pb-1 text-[11px] font-semibold uppercase tracking-wider text-slate-400">Properties</p>
                        {results.properties.map((p) => (
                          <button key={p.property_id} data-testid={`search-result-property-${p.property_id}`}
                            onMouseDown={(e) => { e.preventDefault(); goto(`/properties/${p.property_id}`); }}
                            className="w-full text-left px-4 py-2 hover:bg-slate-50 flex items-center gap-2.5">
                            <Building2 size={15} className="text-slate-400 shrink-0" />
                            <span className="min-w-0"><span className="text-sm font-medium text-slate-800 truncate block">{p.address}</span>
                              <span className="text-xs text-slate-400">{[p.reference, p.city].filter(Boolean).join(" · ")}</span></span>
                          </button>
                        ))}
                      </div>
                    )}
                    {results.contacts?.length > 0 && (
                      <div className="border-t border-slate-100">
                        <p className="px-4 pt-3 pb-1 text-[11px] font-semibold uppercase tracking-wider text-slate-400">Contacts</p>
                        {results.contacts.map((c) => (
                          <button key={c.contact_id} data-testid={`search-result-contact-${c.contact_id}`}
                            onMouseDown={(e) => { e.preventDefault(); goto(`/contacts?q=${encodeURIComponent(c.name || c.email || "")}`); }}
                            className="w-full text-left px-4 py-2 hover:bg-slate-50 flex items-center gap-2.5">
                            <Contact2 size={15} className={`${CONTACT_CLR[c.type] || "text-slate-400"} shrink-0`} />
                            <span className="min-w-0"><span className="text-sm font-medium text-slate-800 truncate block">{c.name || c.email}</span>
                              <span className="text-xs text-slate-400 capitalize">{[c.type, c.email].filter(Boolean).join(" · ")}</span></span>
                          </button>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
          <div className="ml-auto flex items-center gap-3">
            <button onClick={() => navigate("/notifications")} className="relative w-9 h-9 rounded-lg hover:bg-slate-100 flex items-center justify-center text-slate-600 transition-colors">
              <Bell size={18} />
              {unread > 0 && <span className="absolute top-2 right-2 w-2 h-2 bg-blue-600 rounded-full" />}
            </button>
            <button data-testid="header-settings-button" onClick={() => navigate("/settings")} title="Settings" className="w-9 h-9 rounded-lg hover:bg-slate-100 flex items-center justify-center text-slate-600 transition-colors">
              <Settings size={18} />
            </button>
          </div>
        </header>

        <main className="flex-1 p-8">
          <div className="max-w-7xl mx-auto">
            {(title || subtitle) && (
              <div className="mb-8 fade-up">
                {subtitle && <p className="text-xs font-medium uppercase tracking-[0.2em] text-blue-700 mb-2">{subtitle}</p>}
                <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900">{title}</h1>
              </div>
            )}
            {children}
          </div>
        </main>
      </div>
    </div>
  );
}

export { ChevronRight };
